import {useMemo} from 'react';
import {useSelector, shallowEqual} from 'react-redux';

import useResolvedNavigationItem from 'coreHooks/useResolvedNavigationItem';
import {selectNavigationStackTree} from 'coreStore/data/ui';
import {createUrlSearchParameters} from 'coreUtils/url';

import {useHeaderCtx} from 'brandComponents/Header/HeaderCtx';

import {
	ENavigationItemViewAll,
	INavigationItemProps,
	INavigationItemOldProps,
	ENavigationItemsSortingOrder,
} from '../INavigationItem';
import {getAnimationClasses, getSortedChildrenByOrder} from '../helpers';

const useParsedNavigationProps = (props: INavigationItemOldProps) => {
	const currentItemId = props?.sys?.id;
	let children = props?.children;
	const items = props?.subItemsCollection?.items || [];
	const navigationStack = useSelector((state) => selectNavigationStackTree(state, currentItemId), shallowEqual);
	const headerCtx = useHeaderCtx(true);
	const {resolvedTitle, resolvedUrl} = useResolvedNavigationItem(props);

	// Used to determine if the item has direct nested items with nested lists
	const hasSubLists = useMemo(
		() => items.some((item) => item?.subItems?.total || item?.subItems?.props?.total),
		[props],
	);

	// Enqueue the subItems
	const isSubItemsFetched = !!props?.subItemsCollection?.items?.length;

	// Check whether sorting order is set.Sorting only navigation items without personalized blocks
	const isSortingOrder = props.sorting === ENavigationItemsSortingOrder.ASCEND
		|| props.sorting === ENavigationItemsSortingOrder.DESCEND;
	const shouldBeSorted =
		isSortingOrder &&
		isSubItemsFetched &&
		!!props?.children.length &&
		props?.subItems?.props?.total > 0;

	if (shouldBeSorted) {
		children = getSortedChildrenByOrder(props.children, props.sorting);
	}

	const category = props.category?.items?.[0]?.id;

	const newProps: INavigationItemProps = {
		id: currentItemId,
		url: resolvedUrl,
		template: props?.template || null,
		children,
		title: resolvedTitle,
		color: props.color || props.Color,
		hasSubLists,
		parent: props?.parent || null,
		isInHeaderContext: headerCtx?.isInHeaderContext || false,
		description: props?.description,
		isFlatSubList: props?.isFlatSubList,
		category,
		editorialContent: props?.editorialContent,
		image: props?.media || props?.image,
		imageTitle: props?.imageTitle,
		customSettings: props?.customSettings,
		viewAll: {
			url: resolvedUrl,
			show: props?.viewAll ? ENavigationItemViewAll[props.viewAll] : ENavigationItemViewAll['None'],
		},
		requiresLogin: props?.requiresLogin || false
	};

	if (props?.navigationParams) {
		newProps.navigationParams = props.navigationParams;
	}

	if (props?.queryParameters) {
		newProps.queryParameters = createUrlSearchParameters(props.queryParameters);
	}

	// Mobile navigation props
	if (newProps.isInHeaderContext && newProps?.parent?.id) {
		const {handleNavigationItemClick} = headerCtx;

		newProps.hasActive = items.some((item) => typeof navigationStack[item?.sys?.id] !== 'undefined');
		newProps.isActive = typeof navigationStack[currentItemId] !== 'undefined';
		newProps.handleNavigationItemClick = handleNavigationItemClick(currentItemId, newProps?.parent.id);
		newProps.animationClasses = getAnimationClasses(navigationStack, currentItemId);
	}

	return newProps;
};

export default useParsedNavigationProps;
